/**
 * Where the paper's blank space is, and how long it is.
 *
 * A kitchen chit is torn against the printer's bar, so the top margin
 * (Ticket Appearance "Top margin", `headerSpacing`) is the part the cook never
 * reads, and the feed before the cut is the tail under the last line. Both are
 * LINE FEEDS, and a line feed on an empty line advances the printer's line
 * spacing — a property of the printer, not of the character size:
 *
 *   80 mm / 58 mm thermal (TM-m30III)  3.75 mm (30 dots at 203 dpi), Epson default
 *   76 mm impact (TM-U220B)            4.23 mm (1/6 in), Epson default
 *
 * Nothing in the fleet changes the line spacing (no ESC 2 / ESC 3 is emitted),
 * so these defaults are the pitch. They are approximate in the way any
 * mechanism is — the model's published default — and the preview says "≈".
 *
 * The cutter adds its own feed: GS V 66 feeds the paper from the print head to
 * the blade before cutting, so the tail on paper is the feed lines PLUS that
 * fixed distance, which the preview names rather than guesses.
 */
import type { PaperGrid } from './paperGrid';
import { lineColumns } from './textLayout';

/** The printer's default line pitch, in millimetres. */
export function linePitchMm(paperWidthMm: number): number {
  return paperWidthMm === 76 ? 25.4 / 6 : 3.75;
}

/** The same pitch in 203 dpi dots, the preview's drawing unit. */
export function linePitchDots(paperWidthMm: number): number {
  return Math.round((linePitchMm(paperWidthMm) * 203) / 25.4);
}

export interface PaperBlankRun {
  /** Index of the first blank row. */
  readonly startRow: number;
  readonly lines: number;
  readonly mm: number;
}

export interface PaperMargins {
  /** Blank lines above the first printed line (the top margin), if any. */
  readonly top: PaperBlankRun | null;
  /** Blank lines directly above each cut (the feed before cut). */
  readonly beforeCut: readonly PaperBlankRun[];
}

/** The top margin and each feed-before-cut, as the grid prints them. */
export function paperMargins(grid: PaperGrid): PaperMargins {
  const pitch = linePitchMm(grid.paperWidthMm);
  const run = (startRow: number, lines: number): PaperBlankRun => ({
    startRow,
    lines,
    mm: Math.round(lines * pitch * 10) / 10,
  });
  let lead = 0;
  while (lead < grid.rows.length && grid.rows[lead].t === 'blank') lead += 1;
  const beforeCut: PaperBlankRun[] = [];
  grid.rows.forEach((row, i) => {
    if (row.t !== 'cut') return;
    let n = 0;
    while (i - 1 - n >= 0 && grid.rows[i - 1 - n].t === 'blank') n += 1;
    if (n > 0 && i - n > 0) beforeCut.push(run(i - n, n));
  });
  return { top: lead > 0 && lead < grid.rows.length ? run(0, lead) : null, beforeCut };
}

// ─── How much paper a grid uses ─────────────────────────────────────────────
//
// The length of paper a printed document pulls off the roll, for the paper
// supplies tally (design 2026-10-26 §A). The same rows the preview draws,
// measured the way the printer advances them:
//
//   text   one line pitch — or the glyph's height when a taller size
//          (`h` > 1) exceeds the pitch: ESC/POS feeds by the larger of the two
//   blank  one line pitch
//   feed   its `ESC J` dots
//   image  its raster rows (an undecoded image counts IMAGE_FALLBACK_DOTS)
//   qr     the Model-2 symbol the gateway prints: module size 6 dots, error
//          correction level M (escpos.go qrCodeCommand), sized by its data
//   cut    the cutter's own feed (GS V 66), CUT_FEED_MM
//
// An estimate in the way any mechanism is — the cut feed in particular is not
// a published figure pinned anywhere in the repo — and the paper page says
// "≈". A location's counts are what correct it.

/** Paper fed from the print head to the blade on a cut (GS V 66), estimated. */
export const CUT_FEED_MM = 14;

/** Rows an undecodable raster image is counted as (≈ 15 mm at 203 dpi). */
export const IMAGE_FALLBACK_DOTS = 120;

const DOTS_PER_MM = 203 / 25.4;
const QR_MODULE_DOTS = 6;
/** Model 2, level M, byte mode: the most bytes each version holds (v1..v40). */
const QR_M_BYTE_CAPACITY = [
  14, 26, 42, 62, 84, 106, 122, 152, 180, 213, 251, 287, 331, 362, 412, 450, 504, 560, 624, 666,
  711, 779, 857, 911, 997, 1059, 1125, 1190, 1264, 1370, 1452, 1538, 1628, 1722, 1809, 1911, 1989,
  2099, 2213, 2331,
];

/** The QR symbol's height in dots for `data` (no quiet zone: the printer does not feed one). */
function qrDots(data: string | undefined): number {
  const bytes = new TextEncoder().encode(data ?? '').length;
  const index = QR_M_BYTE_CAPACITY.findIndex((cap) => bytes <= cap);
  const version = index === -1 ? 40 : index + 1;
  return (17 + 4 * version) * QR_MODULE_DOTS;
}

/** The paper `grid` uses, in millimetres (one decimal). */
export function paperLengthMm(grid: PaperGrid): number {
  const pitch = linePitchMm(grid.paperWidthMm);
  let total = 0;
  for (const row of grid.rows) {
    switch (row.t) {
      case 'text': {
        let tallestDots = 0;
        for (const run of row.runs) {
          tallestDots = Math.max(tallestDots, (run.fontB ? 17 : 24) * Math.max(1, run.h));
        }
        total += Math.max(pitch, tallestDots / DOTS_PER_MM);
        break;
      }
      case 'blank':
        total += pitch;
        break;
      case 'feed':
        total += Math.max(0, row.dots) / DOTS_PER_MM;
        break;
      case 'image':
        total += (typeof row.h === 'number' && row.h > 0 ? row.h : IMAGE_FALLBACK_DOTS) / DOTS_PER_MM;
        break;
      case 'qr':
        total += qrDots(row.data) / DOTS_PER_MM;
        break;
      case 'cut':
        total += CUT_FEED_MM;
        break;
      default:
        break; // buzzer, pulse: no paper
    }
  }
  return Math.round(total * 10) / 10;
}

// ─── How much ribbon a grid uses ────────────────────────────────────────────
//
// An impact head (TM-U220B, the 76 mm kitchen printer) strikes the ribbon once
// per dot it prints, so a ribbon wears with the characters struck — the unit
// Epson rates ribbon life in (ERC-38 B/R: 1.5 million black, 750,000 red;
// ERC-38 B: 3 million). A run counts its non-blank columns (a Chinese
// double-byte glyph is two), weighted by how many more dots it strikes than a
// 1x1 Font A character:
//
//   width x height multiplier   each doubles the dots of the glyph
//   bold                        the head strikes twice (double-strike)
//   Font B                      a 9x17 cell against 12x24: about half
//   inverted                    every column is struck, blanks included
//
// Red runs (the ribbon's second colour, `alert+`) wear the red band, the rest
// the black. Images and QR codes are not counted: the impact head has no
// graphics path, so the fleet never sends one to it.

export interface RibbonStrikes {
  /** Font A 1x1 characters' worth struck on the black band. */
  readonly black: number;
  /** The same on the red band. */
  readonly red: number;
}

/** The ribbon `grid` uses, in standard characters per colour (whole numbers). */
export function ribbonStrikes(grid: PaperGrid): RibbonStrikes {
  const charset = grid.characterSet === 'Chinese' ? 'Chinese' : 'Western';
  let black = 0;
  let red = 0;
  for (const row of grid.rows) {
    if (row.t !== 'text') continue;
    for (const run of row.runs) {
      const columns = run.inv ? lineColumns(run.s, charset) : lineColumns(run.s.replace(/\s/g, ''), charset);
      if (columns === 0) continue;
      const weight = Math.max(1, run.w) * Math.max(1, run.h) * (run.bold ? 2 : 1) * (run.fontB ? 0.5 : 1);
      if (run.red) red += columns * weight;
      else black += columns * weight;
    }
  }
  return { black: Math.round(black), red: Math.round(red) };
}
